import React, { useEffect, useMemo, Fragment } from 'react';
import { arrayOf, bool, oneOf } from 'prop-types';
import classnames from 'classnames';
import { connectHits } from 'react-instantsearch-core';
import compose from 'lodash/flowRight';
import LazyLoad from 'vanilla-lazyload/dist/lazyload.min';
import { FormattedMessage } from 'react-intl';
import { pausableOnAutoScroll } from 'connectors';
import {
    useFeatureFlags,
    useUser,
    useCategory,
    useLandingProduct,
    useSponsoredProducts,
    useSalePeriod,
    useEnvironmentData,
    useAddToCart,
    useCurrency,
    useLocale,
    useGTM,
} from 'hooks';
import { getProductBanner, category as categoryUtils } from 'utils';
import { productShape } from 'shapes';
import ProductCardRedesigned from 'modules/ProductCardContainer/ProductCardContainer';
import { PRODUCT_CARD_MODE } from 'components/ProductCard/ProductCard.utils';
import {
    OldProduct,
    PromotedProduct,
    InGridElementsWrapper,
    SeoProductsMicrodata,
    MiddleBanner,
} from './components';
import './ProductsListing.scss';
import {
    getInGridElementsMode,
    getSponsoredProductData,
    getSponsoredProductDisplayed,
    separatePromotedProductAndOtherProducts,
    isNewProductCard,
} from './ProductsListing.utils';
import './ProductsListing.old.scss';

let lazyload;

function Products({
    hits,
    showTipsheet,
    showQuickFilters,
    isInCategorySearch,
    mode,
    showPromotedProduct,
}) {
    useEffect(() => {
        lazyload = new LazyLoad({
            elements_selector: '.lazyload',
        });

        return () => {
            lazyload.destroy();
        };
    }, []);

    useEffect(() => {
        lazyload.update();
    });

    const [
        {
            hasB2BProductCardRedesign,
            hasB2B,
            hasSearchEngine,
            hasBingo,
            hasProVAT,
            hasCTAProductCardsHidden,
        },
    ] = useFeatureFlags({
        hasB2BProductCardRedesign: 'ff.browsing.b2b.listing.card.style',
        hasB2B: 'ff.global.b2b',
        hasSearchEngine: 'relevance.search.ranking',
        hasBingo: 'default.bingo',
        hasProVAT: 'ff.browsing.pro.vat',
        hasCTAProductCardsHidden: 'browsing.listing.hide.addtocart.npc',
    });

    const user = useUser();
    const category = useCategory();
    const [landingProductModelId] = useLandingProduct();
    const [sponsoredProducts] = useSponsoredProducts();
    const isSalePeriod = useSalePeriod();
    const { platform, cdn } = useEnvironmentData();
    const addToCart = useAddToCart();
    const [currency] = useCurrency();
    const [locale] = useLocale();
    const [, pushToGTM] = useGTM();

    const isCategory = categoryUtils.isCategory(category);
    const isRedesignedCard = isNewProductCard({
        isB2BUser: user?.isB2B,
        hasB2BProductCardRedesign,
        hasB2B,
    });

    const ProductCard = isRedesignedCard ? ProductCardRedesigned : OldProduct;

    const inGridElementMode = getInGridElementsMode(mode);

    const [promotedProduct, products] = separatePromotedProductAndOtherProducts(
        hits,
        {
            hasAlternateEngine: hasSearchEngine,
            category,
            landingProductModelId,
            showPromotedProduct,
        },
    );

    const promotedProductClassnames = classnames(
        'products__product',
        'products__promoted-product',
        {
            'products__promoted-product--old': !isRedesignedCard,
        },
    );

    const sponsoredProductsDisplayed = useMemo(
        () => getSponsoredProductDisplayed(products, sponsoredProducts),
        [products, sponsoredProducts],
    );

    useEffect(() => {
        if (sponsoredProductsDisplayed.length > 0) {
            pushToGTM({
                event: 'sponsored_products_displayed',
                sponsored_products: sponsoredProductsDisplayed.map(
                    ({ product }) => product,
                ),
                ads_positions: sponsoredProductsDisplayed.map(
                    ({ position }) => position,
                ),
            });
        }
    }, [sponsoredProductsDisplayed.length]);

    return hits.length > 0 ? (
        <Fragment>
            {promotedProduct && (
                <PromotedProduct
                    className={promotedProductClassnames}
                    value={promotedProduct}
                    categoryId={(category || {}).id}
                    redesignedCard={isRedesignedCard}
                    mode={mode}
                    user={user}
                    isSalePeriod={isSalePeriod}
                    platform={platform}
                    cdn={cdn}
                    currency={currency}
                    locale={locale}
                    hasB2B={hasB2B}
                    hasSearchEngine={hasSearchEngine}
                    hasBingo={hasBingo}
                    hasProVAT={hasProVAT}
                    onAddToCart={addToCart}
                />
            )}

            {products.map((product, index) => (
                <Fragment key={product.model_id}>
                    <ProductCard
                        key={product.model_id}
                        className={classnames('products__product', {
                            'products__product--old': !isRedesignedCard,
                            'products__product--recommended':
                                getProductBanner(product, category?.id) ===
                                'recommended',
                        })}
                        lazyloadImage={index > 3}
                        dataPosition={index}
                        product={product}
                        categoryId={(category || {}).id}
                        mode={mode}
                        dataSponsored={getSponsoredProductData(
                            product.objectID,
                            sponsoredProducts,
                        )}
                        user={user}
                        isSalePeriod={isSalePeriod}
                        platform={platform}
                        cdn={cdn}
                        currency={currency}
                        locale={locale}
                        hasB2B={hasB2B}
                        hasSearchEngine={hasSearchEngine}
                        hasBingo={hasBingo}
                        hasProVAT={hasProVAT}
                        onAddToCart={addToCart}
                        withActionBlock={!hasCTAProductCardsHidden}
                    />

                    {isCategory && (
                        <InGridElementsWrapper
                            showQuickFilters={showQuickFilters}
                            showTipsheet={showTipsheet}
                            index={index + 1}
                            nbOfProductCards={hits.length}
                            isCategorySearch={isInCategorySearch}
                            mode={inGridElementMode}
                        />
                    )}
                </Fragment>
            ))}

            <MiddleBanner />

            <SeoProductsMicrodata hits={hits} />
        </Fragment>
    ) : (
        <div className="products-no-results">
            <FormattedMessage
                id="listing.products.noResult"
                defaultMessage="No result found"
                description="No product found message"
            />
        </div>
    );
}

Products.propTypes = {
    hits: arrayOf(productShape),
    showTipsheet: bool,
    showQuickFilters: bool,
    showPromotedProduct: bool,
    isInCategorySearch: bool,
    mode: oneOf([
        PRODUCT_CARD_MODE.GRID,
        PRODUCT_CARD_MODE.GRID_VISUAL,
        PRODUCT_CARD_MODE.LIST_SPEC,
        PRODUCT_CARD_MODE.LIST,
    ]).isRequired,
};

Products.defaultProps = {
    hits: [],
    showPromotedProduct: false,
    showTipsheet: false,
    showQuickFilters: false,
    isInCategorySearch: false,
};

export default compose(connectHits, pausableOnAutoScroll)(Products);
